import type { IsoDate, Measurement } from "./types.js";
import type { HistoryInput } from "./progression.js";
import type { MeasurementView } from "./measurement-view.js";
import { questHistory } from "./progression.js";
import { STREAK_THRESHOLD } from "./scoring.js";
import { rollingAverage } from "./measurements.js";

/**
 * 成就徽章：一次達成就永久成立的里程碑。
 *
 * 由歷史紀錄即時推算，**不新增任何儲存**——與等級、連勝同一路數。
 * 與 Rank 相對：Rank 每天重算，徽章拿到就不會掉。
 *
 * 與 src/domain/ 的其他模組一樣不讀系統時間：「今天」由呼叫端傳入。
 */

export interface Badge {
  id: string;
  label: string;
  /** 未達成時要顯示的條件。達成之後也留著，讓人知道當初是怎麼拿到的。 */
  condition: string;
  /**
   * 刻在徽章面上的字，兩三個字元。
   *
   * 牆上只放得下這麼多——名稱與條件要點開才看得到，所以這個字必須自己就
   * 認得出是哪一枚。
   */
  mark: string;
  /** Rank 類或體重里程碑。決定徽章的顏色。 */
  kind: "rank" | "weight";
  earned: boolean;
  /** 達成的那一天。未達成是 null。 */
  earnedOn: IsoDate | null;
}

/** 只有兩類徽章：Rank 類與體重里程碑。 */
const WEIGHT_MILESTONES = [78, 77, 76, 75];
const S_COUNT_TARGET = 10;
const STREAK_TARGETS = [7, 14];
/** 體重里程碑一律看 7 日平均，不看單日。 */
const WEIGHT_WINDOW = 7;
/** 萬步俱樂部的門檻。看單日，不看平均——它記的是「那天走了很多」這件事。 */
const BIG_STEPS_TARGET = 20000;

/** 遮蔽時體重徽章顯示的字。名稱與條件都是它。 */
const MASKED = "？";

function badge(
  id: string, label: string, condition: string,
  mark: string, kind: Badge["kind"], earnedOn: IsoDate | null
): Badge {
  return { id, label, condition, mark, kind, earned: earnedOn !== null, earnedOn };
}

/**
 * Rank 類。
 *
 * 連勝看的是**歷史上最長那一段**有沒有跨過門檻，不是今天的連勝——
 * 徽章拿到就不會掉，用當下的連勝算會讓它斷掉時消失。
 */
function rankBadges(input: HistoryInput, today: IsoDate): Badge[] {
  const rows = questHistory(input, today);

  let firstS: IsoDate | null = null;
  let sCount = 0;
  let tenthS: IsoDate | null = null;
  let streak = 0;
  const streakReached = new Map<number, IsoDate>();

  for (const row of rows) {
    if (row.rank.grade === "S") {
      sCount++;
      if (firstS === null) firstS = row.date;
      if (sCount === S_COUNT_TARGET) tenthS = row.date;
    }

    streak = row.progress.done >= STREAK_THRESHOLD ? streak + 1 : 0;
    for (const target of STREAK_TARGETS) {
      if (streak === target && !streakReached.has(target)) streakReached.set(target, row.date);
    }
  }

  return [
    badge("first-s", "初次全清", "單日五項核心任務全部完成", "S", "rank", firstS),
    badge("ten-s", "十次全清", `累計 ${S_COUNT_TARGET} 天拿到 S Rank`,
      `S${S_COUNT_TARGET}`, "rank", tenthS),
    ...STREAK_TARGETS.map(target =>
      badge(`streak-${target}`, `${target} 日連勝`, `連續 ${target} 天達成 A Rank 以上`,
        `${target}日`, "rank", streakReached.get(target) ?? null)),
    bigStepsBadge(input, today)
  ];
}

/**
 * 萬步俱樂部。
 *
 * 歸在 Rank 類而不是自成一類：`kind` 只有兩種顏色，加第三種要動 `main.css`
 * 的選取器，而那會靜默失效（鐵則 7）。而且它與體重無關，監督者在揭曉日
 * 之前照樣看得到——那正是這一枚被加進來的理由。
 */
function bigStepsBadge(input: HistoryInput, today: IsoDate): Badge {
  const hit = Object.keys(input.healthMetrics)
    .filter(date => date <= today)
    .sort()
    .find(date => (input.healthMetrics[date]?.steps_count ?? 0) >= BIG_STEPS_TARGET);

  return badge(
    "big-steps",
    "萬步俱樂部",
    `單日步數達到 ${BIG_STEPS_TARGET.toLocaleString("zh-TW")} 步`,
    "20K",
    "rank",
    hit ?? null
  );
}

/** 體重里程碑。以 7 日平均首次低於門檻的那一天為準。 */
function weightBadges(measurements: Measurement[], today: IsoDate): Badge[] {
  const rows = rollingAverage(
    measurements
      .filter(row => row.measurement_date <= today && Number.isFinite(row.weight_kg))
      .sort((a, b) => a.measurement_date.localeCompare(b.measurement_date)),
    "weight_kg",
    WEIGHT_WINDOW
  );

  return WEIGHT_MILESTONES.map(threshold => {
    const hit = rows.find(row => {
      const average = row[`weight_kg_avg${WEIGHT_WINDOW}`] as number | null;
      return Number.isFinite(average as number) && (average as number) < threshold;
    });
    return badge(
      `weight-${threshold}`,
      `突破 ${threshold} kg`,
      `${WEIGHT_WINDOW} 日平均體重低於 ${threshold} kg`,
      String(threshold),
      "weight",
      hit ? hit.measurement_date : null
    );
  });
}

/**
 * 遮蔽版的體重里程碑。
 *
 * **不能拿遮蔽過的量測去跑 `weightBadges()`**——監督者拿到的是無單位的 0–1，
 * 拿去比 78／77／76 會全部低於門檻，算出一整牆假的「已達成」，而且不會報錯。
 * 這一條現在由型別擋著：`MeasurementView` 的遮蔽分支根本沒有 `weight_kg`
 * 那個欄位，`weightBadges()` 收不到它（見 `measurement-view.ts`）。所以這裡
 * 完全不看資料，直接產生固定的問號。
 *
 * 為什麼是「遮起來」而不是「拿掉」：牆上少那幾枚會讓版面塌掉一半，而監督者
 * 看得到可愛的徽章系統本來就是這個功能的目的之一。留著、鎖著、不說是什麼，
 * 揭曉日一到全部補亮。
 */
function maskedWeightBadges(): Badge[] {
  // id 也要換掉。`weight-78` 這種 id 會被渲染進 DOM，等於把門檻階梯直接
  // 印在畫面上——雖然全部未達成、推不出目前體重，但既然規則是不吐公斤級
  // 的數字，就不留這個例外。
  return WEIGHT_MILESTONES.map((_, index) =>
    badge(`weight-masked-${index + 1}`, MASKED, MASKED, MASKED, "weight", null));
}

/**
 * `view` 決定體重那幾枚給不給真的。
 *
 * 先前這裡是第三個參數 `redacted = false`——一個**預設為「給公斤」的布林**，
 * 而漏傳的代價正是這個檔案裡最貴的那個錯。`shared/roles.ts` 的兩支遮蔽函式
 * 都刻意寫成「是本人才給」而不是「是監督者才遮」，理由是漏傳時仍然安全；
 * 那個布林的方向是反的。現在不必判斷了，view 自己知道它是哪一支。
 *
 * `input` 仍然要傳：Rank 類與萬步俱樂部吃的是 `logs` / `healthMetrics`，
 * 與遮蔽無關——萬步俱樂部之所以存在，就是因為它是監督者在揭曉日之前也
 * 看得到的一枚。
 */
export function badges(input: HistoryInput, view: MeasurementView, today: IsoDate): Badge[] {
  return [
    ...rankBadges(input, today),
    ...(view.kind === "shapeOnly" ? maskedWeightBadges() : weightBadges(view.rows, today))
  ];
}
